import { useCallback, useRef } from 'react';

interface Options {
  delayMs?: number;
  disabled?: boolean;
}

/** Touch + mouse long-press; also opens menu on contextmenu (right-click). */
export function useLongPress(onLongPress: () => void, options: Options = {}) {
  const { delayMs = 500, disabled = false } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (disabled) return;
    longPressTriggeredRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      onLongPress();
    }, delayMs);
  }, [clearTimer, delayMs, disabled, onLongPress]);

  const cancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const wrapClick = useCallback(
    (onClick?: () => void) => () => {
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false;
        return;
      }
      onClick?.();
    },
    [],
  );

  const bind = {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onTouchCancel: cancel,
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onContextMenu: (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      onLongPress();
    },
  };

  return { bind, wrapClick };
}
